import {
  Alert,
  Badge,
  Button,
  CollectionToolbar,
  DataTableV2,
  FormField,
  Input,
  Module,
  ModuleBody,
  ModuleHead,
  Textarea,
  useCollection,
  type CollectionColumnDef,
} from "@vjal/ui";
import { useMemo, useState } from "react";
import type { CoachingRole } from "../domain/roles";
import { useCoachingUseCases } from "../hooks/useCoachingZ6Data";
import { useCoachingZ6Mutations } from "../hooks/useCoachingZ6Mutations";
import { humanizeStatus } from "../lib/status-format";
import { Z6BindingStatus } from "./product-z6-shared";

type UseCaseRow = {
  id: string;
  title: string;
  industry: string;
  status: string;
  visibility: string;
  updated: string;
};

export function UseCaseRepositoryPanel({ role }: { role: CoachingRole }) {
  const binding = useCoachingUseCases();
  const { createUseCase } = useCoachingZ6Mutations();
  const [title, setTitle] = useState("");
  const [industry, setIndustry] = useState("");
  const [body, setBody] = useState("");
  const canAuthor = role === "super_admin" || role === "admin" || role === "coach";
  const liveReady = Boolean(binding.isLiveBound);
  const useCases = useMemo(() => binding.data?.useCases ?? [], [binding.data?.useCases]);

  const rows = useMemo<UseCaseRow[]>(
    () =>
      useCases.map((useCase) => ({
        id: String(useCase.id),
        title: useCase.title,
        industry: useCase.industry ?? "—",
        status: String(useCase.status ?? ""),
        visibility: humanizeStatus(useCase.visibility),
        updated: String(useCase.updatedAt ?? ""),
      })),
    [useCases]
  );

  const columns = useMemo<CollectionColumnDef<UseCaseRow>[]>(
    () => [
      {
        id: "title",
        header: "Title",
        searchValue: (row) => row.title,
      },
      {
        id: "industry",
        header: "Industry",
        searchValue: (row) => row.industry,
      },
      {
        id: "status",
        header: "Status",
        searchValue: (row) => row.status,
        cell: (row) => (
          <Badge variant={row.status === "published" ? "success" : "info"}>
            {humanizeStatus(row.status)}
          </Badge>
        ),
      },
      {
        id: "visibility",
        header: "Visibility",
        searchValue: (row) => row.visibility,
      },
      {
        id: "updated",
        header: "Updated",
        searchValue: (row) => row.updated,
      },
    ],
    []
  );

  const collection = useCollection<UseCaseRow>({
    mode: "client",
    rows,
    columns,
    pageSize: 25,
    paginate: false,
    urlSync: false,
    getRowKey: (row) => row.id,
  });

  return (
    <Module span={12}>
      <ModuleHead
        title="Use case repository"
        subtitle="Industry use cases coaches encounter, shared across the Vjal team (F14)."
      />
      <ModuleBody className="grid gap-5 lg:grid-cols-[2fr_1fr]">
        <div className="space-y-3">
          <Z6BindingStatus binding={binding} label="use cases" />
          {!liveReady ? (
            <Alert variant="info">
              Use-case repository data is available when the coaching API binding is live.
            </Alert>
          ) : null}
          {liveReady && binding.data && useCases.length === 0 ? (
            <Alert variant="info">No use cases have been submitted yet.</Alert>
          ) : null}
          {liveReady && useCases.length > 0 ? (
            <div className="space-y-3 rounded-xl border border-line bg-white">
              <CollectionToolbar
                collection={collection}
                searchPlaceholder="Filter use cases"
                searchDebounceMs={0}
                className="rounded-xl border-b border-line"
                stableChipNames
              />
              <DataTableV2
                collection={collection}
                caption="Use case repository"
                emptyTitle="No use cases match your search"
                emptyMessage="Clear or change the search to widen the repository view."
                tableClassName="min-w-[40rem]"
              />
            </div>
          ) : null}
        </div>
        {canAuthor ? (
          <div className="space-y-3">
            <p className="m-0 text-sm font-semibold text-strong">Submit a use case</p>
            {!liveReady ? (
              <Alert variant="info">Submission waits for a live coaching API binding.</Alert>
            ) : null}
            <FormField label="Title">
              {({ id, describedBy }) => (
                <Input
                  id={id}
                  aria-describedby={describedBy}
                  value={title}
                  onChange={(event) => setTitle(event.target.value)}
                  placeholder="Use case title"
                  disabled={!liveReady}
                />
              )}
            </FormField>
            <FormField label="Industry (optional)">
              {({ id, describedBy }) => (
                <Input
                  id={id}
                  aria-describedby={describedBy}
                  value={industry}
                  onChange={(event) => setIndustry(event.target.value)}
                  placeholder="e.g. Manufacturing"
                  disabled={!liveReady}
                />
              )}
            </FormField>
            <FormField label="Details">
              {({ id, describedBy }) => (
                <Textarea
                  id={id}
                  aria-describedby={describedBy}
                  rows={4}
                  value={body}
                  onChange={(event) => setBody(event.target.value)}
                  placeholder="Describe the use case…"
                  disabled={!liveReady}
                />
              )}
            </FormField>
            <Button
              type="button"
              disabled={!liveReady || !title.trim() || !body.trim() || createUseCase.isPending}
              loading={createUseCase.isPending}
              onClick={() => {
                createUseCase.mutate({
                  title: title.trim(),
                  body: body.trim(),
                  industry: industry.trim() || undefined,
                });
                setTitle("");
                setIndustry("");
                setBody("");
              }}
            >
              Submit use case
            </Button>
            {createUseCase.isSuccess ? (
              <Alert variant="success">Use case submitted to the repository.</Alert>
            ) : null}
          </div>
        ) : (
          <Alert variant="info">Use-case submission is available to coaches and admins.</Alert>
        )}
      </ModuleBody>
    </Module>
  );
}
